import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { Download, Filter, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/timeago";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { formatApiError } from "@/lib/errors";

const ALL_ACTIONS = [
  "user.create",
  "user.update",
  "user.disable",
  "user.enable",
  "user.reset_password",
  "user.delete",
  "role.create",
  "role.update",
  "role.delete",
  "app.create",
  "app.update",
  "app.delete",
  "matrix.update",
  "auth.login.success",
  "auth.login.failure",
  "auth.logout",
  "sso.launch.success",
  "sso.launch.failure",
  "sso.key.rotate",
];

const WINDOW_OPTS = [
  { v: "24h", label: "Last 24h" },
  { v: "7d", label: "Last 7 days" },
  { v: "30d", label: "Last 30 days" },
  { v: "all", label: "All time" },
];

function buildQuery(params) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return;
    usp.set(k, Array.isArray(v) ? v.join(",") : String(v));
  });
  return usp.toString();
}

function ActionBadge({ action }) {
  const tone = action.startsWith("auth.login.failure") || action.includes("delete") || action === "user.disable"
    ? "bg-cargodash-alert-600/10 text-cargodash-alert-600"
    : action.startsWith("auth.login.success") || action.endsWith(".create")
    ? "bg-cargodash-primary-50 text-cargodash-primary-700"
    : "bg-slate-100 text-slate-700";
  return (
    <code className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${tone}`}>
      {action}
    </code>
  );
}

function AuditTab() {
  const [filters, setFilters] = useState({ window: "7d", actor_email: "", actions: [], target_type: "" });
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery({
        window: filters.window,
        actor_email: filters.actor_email || undefined,
        actions: filters.actions,
        target_type: filters.target_type || undefined,
        page,
        page_size: pageSize,
      });
      const { data } = await api.get(`/admin/audit?${qs}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAction = (a) =>
    setFilters((f) => ({
      ...f,
      actions: f.actions.includes(a) ? f.actions.filter((x) => x !== a) : [...f.actions, a],
    }));

  const download = async () => {
    const qs = buildQuery({
      window: filters.window,
      actor_email: filters.actor_email || undefined,
      actions: filters.actions,
      target_type: filters.target_type || undefined,
    });
    try {
      const response = await api.get(`/admin/audit/export?${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cargodash-audit.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid="audit-tab">
      <div className="mb-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <div>
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">Window</div>
          <Select value={filters.window} onValueChange={(v) => { setPage(1); setFilters({ ...filters, window: v }); }}>
            <SelectTrigger data-testid="audit-filter-window"><SelectValue /></SelectTrigger>
            <SelectContent>{WINDOW_OPTS.map((o) => (<SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">Actor email</div>
          <Input
            value={filters.actor_email}
            onChange={(e) => { setPage(1); setFilters({ ...filters, actor_email: e.target.value }); }}
            placeholder="user@example.com"
            data-testid="audit-filter-actor"
          />
        </div>
        <div>
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">Target type</div>
          <Select value={filters.target_type || "any"} onValueChange={(v) => { setPage(1); setFilters({ ...filters, target_type: v === "any" ? "" : v }); }}>
            <SelectTrigger data-testid="audit-filter-target"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {["user", "role", "app", "matrix", "session", "sso_key"].map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end justify-end gap-2">
          <Button variant="outline" size="sm" onClick={download} data-testid="audit-export-csv" className="gap-1.5">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-3">
        <Filter className="h-3.5 w-3.5 text-slate-400" />
        <span className="mr-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">Actions</span>
        {ALL_ACTIONS.map((a) => {
          const active = filters.actions.includes(a);
          return (
            <button
              key={a}
              type="button"
              onClick={() => { setPage(1); toggleAction(a); }}
              data-testid={`audit-action-chip-${a}`}
              className={`rounded-full border px-2 py-0.5 font-mono text-[10px] transition ${
                active
                  ? "border-cargodash-primary-500 bg-cargodash-primary-50 text-cargodash-primary-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {a}
            </button>
          );
        })}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Time</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>IP</TableHead>
              <TableHead className="pr-4 text-right">Meta</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-500">Loading…</TableCell></TableRow>)}
            {!loading && data.rows.length === 0 && (<TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-500">No rows match the filters.</TableCell></TableRow>)}
            {data.rows.map((r) => (
              <TableRow key={r.id} data-testid={`audit-row-${r.id}`}>
                <TableCell className="pl-4 text-xs text-slate-500" title={r.ts}>{timeAgo(r.ts)}</TableCell>
                <TableCell><ActionBadge action={r.action} /></TableCell>
                <TableCell className="text-xs text-slate-700">
                  <div>{r.actor_email || <span className="italic text-slate-400">anonymous</span>}</div>
                  {r.actor_role && (<div className="text-[10px] text-slate-400">{r.actor_role}</div>)}
                </TableCell>
                <TableCell className="text-xs text-slate-700">
                  {r.target_label ? (
                    <div>
                      <div>{r.target_label}</div>
                      {r.target_type && (<div className="text-[10px] text-slate-400">{r.target_type}</div>)}
                    </div>
                  ) : (<span className="text-slate-400">—</span>)}
                </TableCell>
                <TableCell className="text-xs text-slate-500">{r.ip || "—"}</TableCell>
                <TableCell className="pr-4 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setActive(r)} data-testid={`audit-row-view-${r.id}`}><Eye className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <div>{data.total} total · page {page} of {totalPages}</div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-3 w-3" /></Button>
          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-3 w-3" /></Button>
        </div>
      </div>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="sm:max-w-xl" data-testid="audit-row-sheet">
          <SheetHeader>
            <SheetTitle>{active?.action}</SheetTitle>
            <SheetDescription>{active?.ts}</SheetDescription>
          </SheetHeader>
          {active && (
            <pre className="mt-4 max-h-[70vh] overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-[11px] font-mono text-slate-800">
{JSON.stringify(active, null, 2)}
            </pre>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function LoginActivityTab() {
  const [data, setData] = useState({ rows: [], chart: [], totals: { success: 0, failure: 0 } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports/login-activity");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="login-activity-tab" className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Successful logins (30d)</div>
          <div className="mt-2 font-display text-3xl font-semibold text-cargodash-primary-700">{data.totals.success}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Failed attempts (30d)</div>
          <div className="mt-2 font-display text-3xl font-semibold text-cargodash-alert-600">{data.totals.failure}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Latest events shown</div>
          <div className="mt-2 font-display text-3xl font-semibold text-slate-900">{data.rows.length}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="login-chart">
        <div className="mb-2 text-sm font-semibold text-slate-900">Logins per day</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.chart} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={2} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <ReTooltip />
              <Bar dataKey="success" stackId="a" fill="#16a34a" radius={[2, 2, 0, 0]} />
              <Bar dataKey="failure" stackId="a" fill="#dc2626" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Time</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="pr-4">IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-500">Loading…</TableCell></TableRow>)}
            {!loading && data.rows.length === 0 && (<TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-slate-500">No login events yet.</TableCell></TableRow>)}
            {data.rows.slice(0, 200).map((r) => {
              const success = r.action === "auth.login.success";
              return (
                <TableRow key={r.id}>
                  <TableCell className="pl-4 text-xs text-slate-500" title={r.ts}>{timeAgo(r.ts)}</TableCell>
                  <TableCell className="text-xs">{r.actor_email || "—"}</TableCell>
                  <TableCell>
                    {success ? (
                      <Badge className="bg-cargodash-primary-50 text-cargodash-primary-700 hover:bg-cargodash-primary-50">success</Badge>
                    ) : (
                      <Badge className="bg-cargodash-alert-600/10 text-cargodash-alert-600 hover:bg-cargodash-alert-600/10">failure</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{r.metadata?.reason || ""}</TableCell>
                  <TableCell className="pr-4 text-xs text-slate-500">{r.ip || "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AppUsageTab() {
  const [data, setData] = useState({ per_app: [], per_user_by_app: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/reports/app-usage");
        setData(data);
        if (data.per_app.length > 0) setSelected(data.per_app[0].app_id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const perUser = data.per_user_by_app[selected] || [];

  return (
    <div data-testid="app-usage-tab" className="grid gap-4 lg:grid-cols-2">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-900">Per-app totals</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">App</TableHead>
              <TableHead>Launches</TableHead>
              <TableHead>Unique users</TableHead>
              <TableHead className="pr-4">Last launch</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (<TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-slate-500">Loading…</TableCell></TableRow>)}
            {!loading && data.per_app.length === 0 && (<TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-slate-500">No launches yet.</TableCell></TableRow>)}
            {data.per_app.map((p) => (
              <TableRow
                key={p.app_id}
                onClick={() => setSelected(p.app_id)}
                className={`cursor-pointer ${selected === p.app_id ? "bg-cargodash-primary-50/40" : ""}`}
                data-testid={`app-usage-row-${p.app_id}`}
              >
                <TableCell className="pl-4 font-medium text-slate-900">{p.app_name}</TableCell>
                <TableCell className="font-mono text-sm">{p.total_launches}</TableCell>
                <TableCell className="font-mono text-sm">{p.unique_users}</TableCell>
                <TableCell className="pr-4 text-xs text-slate-500">{timeAgo(p.last_launched_at) || "never"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-900">
          Per-user totals {selected && data.per_app.find(p => p.app_id === selected) ? `· ${data.per_app.find(p => p.app_id === selected).app_name}` : ""}
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">User</TableHead>
              <TableHead>Launches</TableHead>
              <TableHead className="pr-4">Last launch</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {perUser.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="py-10 text-center text-sm text-slate-500">No launches for this app yet.</TableCell></TableRow>
            ) : perUser.map((u) => (
              <TableRow key={u.user_id}>
                <TableCell className="pl-4">
                  <div className="font-medium text-slate-900">{u.full_name || u.email}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </TableCell>
                <TableCell className="font-mono text-sm">{u.count}</TableCell>
                <TableCell className="pr-4 text-xs text-slate-500">{timeAgo(u.last_launched_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function Reports() {
  const params = new URLSearchParams(window.location.search);
  const initial = params.get("tab") === "logins" ? "logins" : params.get("tab") === "usage" ? "usage" : "audit";

  return (
    <div data-testid="reports-page" className="mx-auto w-full max-w-6xl">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">Reports</h2>
        <p className="mt-1 text-sm text-slate-500">Audit log, login activity and app usage.</p>
      </div>

      <Tabs defaultValue={initial}>
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="audit" data-testid="reports-tab-audit">Audit Log</TabsTrigger>
          <TabsTrigger value="logins" data-testid="reports-tab-logins">Login Activity</TabsTrigger>
          <TabsTrigger value="usage" data-testid="reports-tab-usage">App Usage</TabsTrigger>
        </TabsList>
        <TabsContent value="audit" className="pt-4"><AuditTab /></TabsContent>
        <TabsContent value="logins" className="pt-4"><LoginActivityTab /></TabsContent>
        <TabsContent value="usage" className="pt-4"><AppUsageTab /></TabsContent>
      </Tabs>
    </div>
  );
}
